import React, { useEffect, useState } from 'react';
import {
  Navbar,
  NavbarBrand,
  NavbarContent,
  NavbarItem,
  Button,
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownItem,
  Avatar,
} from '@nextui-org/react';
import {
  Search,
  ChevronDown,
  Trophy,
  Users,
  Beer,
  ShoppingBag,
  CalendarDays,
  BarChart3,
  Mail,
  User as UserIcon,
  LogOut,
  X,
  Heart,
  Sliders,
  Dices,
  MessageCircle,
  ClipboardList,
  Sun,
  Moon,
  Shield,
  Menu,
  Newspaper,
  Plus,
  LifeBuoy,
  MapPinned,
  Medal,
  Landmark,
  Sparkles,
  MessagesSquare,
} from 'lucide-react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useConfirm } from '../hooks/useConfirm';
import { useSearchExplorer } from '../contexts/SearchExplorerContext';
import { useTheme } from '../contexts/ThemeContext';
import { useLanguage } from '../contexts/LanguageContext';
import { t } from '../i18n';
import AppDownloadPopup from './AppDownloadPopup';
import CreateEntryModal from './CreateEntryModal';
import userService from '../services/userService';
import chatService from '../services/chatService';
import useLandingPages from '../hooks/useLandingPages';
import { landingPath } from '../services/landingPageService';

const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const { isAuthenticated, user, logout } = useAuth();
  const { confirm, confirmDialog } = useConfirm();
  const { open: openSearch } = useSearchExplorer();
  const { theme, toggleTheme } = useTheme();
  const { lang, toggleLang } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  // „Lösungen“-Menü (2026-09-20): veröffentlichte Landingpages, EIN Request (Modul-Cache)
  const { pages: solutionPages } = useLandingPages(lang);

  const isActivePath = (href: string) =>
    location.pathname === href || location.pathname.startsWith(`${href}/`);

  // Mobile-Menü: bei Routenwechsel schließen + Body-Scroll sperren solange offen
  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = isMenuOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMenuOpen]);

  // Ungelesene Nachrichten pollen (chatService fängt Fehler ab und liefert 0)
  useEffect(() => {
    if (!isAuthenticated) {
      setUnreadCount(0);
      return;
    }
    let cancelled = false;
    const poll = () => {
      chatService
        .getUnreadCount()
        .then((count) => {
          if (!cancelled) setUnreadCount(count ?? 0);
        })
        .catch(() => {
          /* ignorieren */
        });
    };
    poll();
    const timer = setInterval(poll, 30000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [isAuthenticated]);

  const displayName =
    [user?.firstname, user?.lastname].filter(Boolean).join(' ') ||
    user?.username ||
    user?.email ||
    t('web.component.header.mein-konto');
  const initials = (() => {
    const f = (user?.firstname || '').charAt(0);
    const l = (user?.lastname || '').charAt(0);
    const combined = `${f}${l}`.toUpperCase();
    if (combined) return combined;
    return (user?.username || user?.email || '?').charAt(0).toUpperCase();
  })();
  const avatarUrl = userService.getFileUrl(user?.profileImage);
  const isAdmin = (user as any)?.role === 'ADMIN';

  const handleLogout = async () => {
    const ok = await confirm({
      title: t('web.component.header.wirklich-abmelden'),
      message: t('web.component.header.moechtest-du-dich-von-dartcloud'),
      confirmLabel: t('web.component.header.abmelden'),
      danger: true,
    });
    if (!ok) return;
    logout();
    navigate('/', { replace: true });
  };

  const menuItems = [
    { name: t('web.component.header.home'), href: '/' },
    { name: t('web.component.matches-section.spiele'), href: '/spiele' },
    // Live-Turniere der DartCloud-Turniersoftware
    { name: t('web.component.footer.turniere'), href: '/live' },
    // 2026-09-28: „Pro-Turniere“ (alle Verbände) statt „PDC-Turniere“; /pdc-turniere bleibt als reine PDC-Liste erreichbar
    { name: t('web.page.pdc-tournaments-page.pro-turniere'), href: '/pro-turniere' },
    { name: t('web.page.nearby-page.in-der-naehe'), href: '/in-der-naehe' },
    { name: t('web.component.footer.community'), href: '/community' },
    { name: t('web.component.post-editor.statistiken'), href: '/statistiken' }
  ];

  const moreDropdownItems = [
    {
      name: t('web.component.news-section.news'),
      href: '/news',
      description: t('web.component.header.aktuelles-aus-der-dart-welt'),
      icon: Newspaper,
    },
    {
      name: t('web.component.footer.turniere'),
      href: '/turniere',
      description: t('web.component.header.dart-turniere-finden'),
      icon: Trophy,
    },
    {
      name: t('web.component.footer.vereine'),
      href: '/vereine',
      description: t('web.component.header.dart-vereine-in-deiner-naehe'),
      icon: Users,
    },
    {
      name: t('web.component.footer.spielstaetten'),
      href: '/spielstaetten',
      description: t('web.component.header.kneipen-bars-mit-dart'),
      icon: Beer,
    },
    {
      name: t('web.component.footer.shops'),
      href: '/shops',
      description: t('web.component.header.dart-equipment-kaufen'),
      icon: ShoppingBag,
    },
    {
      name: t('web.component.footer.ligen'),
      href: '/ligen',
      description: t('web.component.header.dart-ligen-spielklassen-saison'),
      icon: Medal,
    },
    {
      name: t('web.component.footer.verbaende'),
      href: '/verbaende',
      description: t('web.component.header.landes-regionalverbaende'),
      icon: Landmark,
    },
    {
      name: t('web.component.footer.turnierserien'),
      href: '/turnierserien',
      description: t('web.component.header.regelmaessige-turnierserien'),
      icon: CalendarDays,
    },
    {
      name: t('web.component.header.ranglisten'),
      href: '/ranglisten',
      description: t('web.component.header.pro-spieler-rankings'),
      icon: BarChart3,
    },
    {
      name: t('web.page.players-directory-page.pro-spieler'),
      href: '/spieler',
      description: t('web.component.header.alle-profis-in-der-uebersicht'),
      icon: UserIcon,
    },
    {
      // Marcel 28.09.2026: /pdc-turniere bleibt (SEO), Hauptmenü zeigt „Pro-Turniere“ → hier der Weg zur PDC-Liste
      name: t('web.component.header.pdc-turniere'),
      href: '/pdc-turniere',
      description: t('web.component.header.alle-turniere-des-pdc-verbands'),
      icon: Medal,
    },
    {
      name: t('web.component.home-prediction-games.tippspiele'),
      href: '/tippspiele',
      description: t('web.component.header.tippen-punkte-sammeln'),
      icon: Dices,
    },
    {
      name: t('web.component.header.darten-in-deiner-stadt'),
      href: '/darten-in',
      description: t('web.component.header.vereine-turniere-shops-nach-ort'),
      icon: MapPinned,
    },
    {
      name: t('web.component.footer.forum'),
      href: '/forum',
      description: t('web.component.header.diskutieren-fragen-stellen'),
      icon: MessagesSquare,
    },
    {
      name: t('web.page.community-page.hilfe'),
      href: '/hilfe',
      description: t('web.component.header.tutorials-anleitungen'),
      icon: LifeBuoy,
    },
    {
      name: t('web.component.footer.kontakt'),
      href: '/kontakt',
      description: t('web.component.header.kontakt-aufnehmen'),
      icon: Mail,
    },
  ];

  const solutionsHubHref = landingPath(null, lang);
  const solutionsItems = solutionPages.map((p) => ({
    name: p.title,
    href: landingPath(p.slug, lang),
    description: p.subtitle,
  }));

  return (
    <>
      {confirmDialog}
      <Navbar
        className="bg-black/95 backdrop-blur-md border-b border-zinc-800 shadow-[0_10px_32px_-20px_rgba(63,206,209,0.35)]"
        maxWidth="full"
        height="4rem"
      >
        <NavbarContent>
          {/* Eigener Burger-Toggle: NextUI NavbarMenuToggle reagierte unzuverlässig */}
          <button
            type="button"
            aria-label={isMenuOpen ? t('web.component.header.menue-schliessen') : t('web.component.header.menue-oeffnen')}
            aria-expanded={isMenuOpen}
            onClick={() => setIsMenuOpen((v) => !v)}
            className="flex h-11 w-11 items-center justify-center rounded-xl text-white active:bg-zinc-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3fced1]/60 xl:hidden"
          >
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
          <NavbarBrand className="flex-grow-0">
            <RouterLink to="/" aria-label={t('web.component.footer.zur-startseite')} className="flex items-center">
              {/* Light Mode: Schwarz-Türkis-Logo (Marcel 2026-09-02) — Umschaltung per html.light in index.css */}
              <img
                src="/DC_Logo_Typo_Blau_gross.png"
                alt={t('web.component.header.dartcloud-logo')}
                className="dc-logo-dark h-7 sm:h-8 md:h-9 w-auto max-w-[120px] sm:max-w-[140px] md:max-w-[160px] object-contain"
              />
              <img
                src="/DC_Logo_Typo_SchwarzBlau_gross.png"
                alt={t('web.component.header.dartcloud-logo')}
                className="dc-logo-light hidden h-7 sm:h-8 md:h-9 w-auto max-w-[120px] sm:max-w-[140px] md:max-w-[160px] object-contain"
              />
            </RouterLink>
          </NavbarBrand>
        </NavbarContent>

        <NavbarContent className="hidden xl:flex gap-7" justify="center">
          {menuItems.map((item) => {
            const active = isActivePath(item.href);
            return (
              <NavbarItem key={item.name} isActive={active}>
                <RouterLink
                  to={item.href}
                  aria-current={active ? 'page' : undefined}
                  className={`relative rounded-md pb-1.5 text-[15px] font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3fced1]/60 ${
                    active ? 'text-white' : 'text-gray-300 hover:text-white'
                  }`}
                >
                  {item.name}
                  <span
                    aria-hidden="true"
                    className={`pointer-events-none absolute inset-x-0 bottom-0 h-[3px] rounded-full bg-gradient-to-r from-[#3fced1] to-[#88eef0] transition-all duration-300 ${
                      active ? 'opacity-100 scale-x-100' : 'opacity-0 scale-x-50'
                    }`}
                  />
                </RouterLink>
              </NavbarItem>
            );
          })}
          
          {/* Lösungen (2026-09-20): Landingpages für Vereine, Spielstätten, Veranstalter … */}
          <NavbarItem isActive={isActivePath(solutionsHubHref)}>
            {solutionsItems.length === 0 ? (
              <RouterLink
                to={solutionsHubHref}
                className="rounded-md text-[15px] font-medium text-gray-300 transition-colors hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3fced1]/60"
              >
                {t('web.component.header.loesungen')}
              </RouterLink>
            ) : (
              <Dropdown placement="bottom" classNames={{ content: t('web.component.header.p-0-bg-zinc-950') }}>
                <DropdownTrigger>
                  <button
                    type="button"
                    className="group inline-flex items-center gap-1 rounded-md border-0 bg-transparent p-0 text-[15px] font-medium text-gray-300 transition-colors hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-[#3fced1]/60"
                    data-testid="header-solutions-trigger"
                  >
                    <span>{t('web.component.header.loesungen')}</span>
                    <ChevronDown size={16} className="transition-transform duration-200 group-data-[open=true]:rotate-180" />
                  </button>
                </DropdownTrigger>
                <DropdownMenu
                  aria-label={t('web.component.header.loesungen-menue')}
                  classNames={{ base: 'w-[520px] p-3 outline-none', list: t('web.component.header.grid-grid-cols-2-gap') }}
                  itemClasses={{
                    base: ['rounded-xl', 'px-3 py-2.5', 'transition-colors', 'data-[hover=true]:bg-zinc-800/70', 'data-[selectable=true]:focus:bg-zinc-800/70'].join(' '),
                  }}
                >
                  {[
                    ...solutionsItems.map((item) => (
                      <DropdownItem key={item.href} href={item.href} textValue={item.name}>
                        <div className="flex flex-col">
                          <span className="text-sm font-semibold text-white">{item.name}</span>
                          {item.description && <span className="line-clamp-1 text-xs text-gray-400">{item.description}</span>}
                        </div>
                      </DropdownItem>
                    )),
                    <DropdownItem
                      key={solutionsHubHref}
                      href={solutionsHubHref}
                      textValue={t('web.component.header.alle-loesungen')}
                      startContent={
                        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#3fced1]/10 text-[#3fced1]">
                          <Sparkles size={18} />
                        </span>
                      }
                    >
                      <div className="flex flex-col">
                        <span className="text-sm font-semibold text-white">{t('web.component.header.alle-loesungen')}</span>
                        <span className="text-xs text-gray-400">{t('web.component.header.dartcloud-fuer-vereine-spielstaetten')}</span>
                      </div>
                    </DropdownItem>,
                  ]}
                </DropdownMenu>
              </Dropdown>
            )}
          </NavbarItem>

          {/* Mehr Dropdown */}
          <NavbarItem>
            <Dropdown
              placement="bottom"
              classNames={{
                content:
                  t('web.component.header.p-0-bg-zinc-950'),
              }}
            >
              <DropdownTrigger>
                <button
                  type="button"
                  className="group inline-flex items-center gap-1 rounded-md border-0 bg-transparent p-0 text-[15px] font-medium text-gray-300 transition-colors hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-[#3fced1]/60"
                >
                  <span>{t('web.component.header.mehr')}</span>
                  <ChevronDown
                    size={16}
                    className="transition-transform duration-200 group-data-[open=true]:rotate-180"
                  />
                </button>
              </DropdownTrigger>
              <DropdownMenu
                aria-label={t('web.component.header.mehr-menue')}
                classNames={{
                  base: 'w-[560px] p-3 outline-none',
                  list: t('web.component.header.grid-grid-cols-2-gap'),
                }}
                itemClasses={{
                  base: [
                    'rounded-xl',
                    'px-3 py-2.5',
                    'transition-colors',
                    'data-[hover=true]:bg-zinc-800/70',
                    'data-[selectable=true]:focus:bg-zinc-800/70',
                  ].join(' '),
                }}
              >
                {moreDropdownItems.map((item) => {
                  const Icon = item.icon;
                  return (
                    <DropdownItem
                      key={item.name}
                      href={item.href}
                      textValue={item.name}
                      startContent={
                        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#3fced1]/10 text-[#3fced1]">
                          <Icon size={18} />
                        </span>
                      }
                    >
                      <div className="flex flex-col">
                        <span className="text-sm font-semibold text-white">
                          {item.name}
                        </span>
                        <span className="text-xs text-gray-400">
                          {item.description}
                        </span>
                      </div>
                    </DropdownItem>
                  );
                })}
              </DropdownMenu>
            </Dropdown>
          </NavbarItem>
        </NavbarContent>

        <NavbarContent justify="end" className="gap-2">
          <NavbarItem className="flex items-center">
            <button
              type="button"
              aria-label={t('web.component.header.suche-oeffnen')}
              onClick={openSearch}
              className="inline-flex h-10 w-10 items-center justify-center rounded-xl text-gray-400 transition-colors hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3fced1]/60"
            >
              <Search size={20} />
            </button>
          </NavbarItem>

          <NavbarItem className="hidden xl:flex items-center">
            <button
              type="button"
              aria-label={theme === 'dark' ? t('web.header.theme.activateLight') : t('web.header.theme.activateDark')}
              title={theme === 'dark' ? t('web.header.theme.light') : t('web.header.theme.dark')}
              onClick={toggleTheme}
              className="inline-flex h-10 w-10 items-center justify-center rounded-xl text-gray-400 transition-colors hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3fced1]/60"
            >
              {theme === 'dark' ? <Sun size={20} /> : <Moon size={20} />}
            </button>
          </NavbarItem>

          {/* Sprachumschalter DE/EN (Mehrsprachigkeit 2026-09-17) */}
          <NavbarItem className="hidden xl:flex items-center">
            <button
              type="button"
              aria-label={lang === 'de' ? t('web.header.language.switchTo.en') : t('web.header.language.switchTo.de')}
              title={lang === 'de' ? t('web.header.language.switchTo.en') : t('web.header.language.switchTo.de')}
              onClick={() => { toggleLang(); }}
              className="inline-flex h-10 min-w-10 items-center justify-center rounded-xl px-2 text-xs font-bold tracking-wide text-gray-400 transition-colors hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3fced1]/60"
            >
              {lang === 'de' ? 'EN' : 'DE'}
            </button>
          </NavbarItem>

          {isAuthenticated ? (
            <NavbarItem>
              <Dropdown placement="bottom-end">
                <DropdownTrigger>
                  <Button
                    variant="light"
                    className="text-white gap-2 px-2"
                    startContent={
                      <Avatar
                        src={avatarUrl || undefined}
                        name={initials}
                        showFallback
                        size="sm"
                        className="h-8 w-8 bg-[#3fced1] text-black text-sm font-semibold"
                      />
                    }
                    endContent={<ChevronDown size={16} className="text-gray-400" />}
                  >
                    <span className="hidden md:inline max-w-[140px] truncate text-sm">
                      {displayName}
                    </span>
                  </Button>
                </DropdownTrigger>
                <DropdownMenu aria-label={t('web.component.header.konto')}>
                  {[
                    <DropdownItem key="profile" startContent={<UserIcon size={16} />}
                      onPress={() => navigate('/profil')}>
                      {t('web.component.header.profil')}
                    </DropdownItem>,
                    <DropdownItem key="favorites" startContent={<Heart size={16} />}
                      onPress={() => navigate('/favoriten')}>
                      {t('web.component.header.favoriten')}
                    </DropdownItem>,
                    <DropdownItem key="registrations" startContent={<ClipboardList size={16} />}
                      onPress={() => navigate('/meine-anmeldungen')}>
                      {t('web.component.header.meine-anmeldungen')}
                    </DropdownItem>,
                    <DropdownItem key="messages" startContent={<MessageCircle size={16} />}
                      onPress={() => navigate('/nachrichten')}>
                      {unreadCount > 0 ? t('web.component.header.nachrichten', { unreadCount }) : t('web.component.header.nachrichten-2')}
                    </DropdownItem>,
                    <DropdownItem key="management" startContent={<Sliders size={16} />}
                      onPress={() => navigate('/verwaltung')}>
                      {t('web.component.header.verwaltung')}
                    </DropdownItem>,
                    <DropdownItem key="create" startContent={<Plus size={16} />}
                      onPress={() => setIsCreateOpen(true)}>
                      {t('web.component.header.erstellen')}
                    </DropdownItem>,
                    ...(isAdmin
                      ? [
                          <DropdownItem key="admin" startContent={<Shield size={16} />}
                            onPress={() => navigate('/admin')}>
                            {t('web.component.header.admin-bereich')}
                          </DropdownItem>,
                        ]
                      : []),
                    <DropdownItem
                      key="logout"
                      color="danger"
                      startContent={<LogOut size={16} />}
                      onPress={handleLogout}
                    >
                      {t('web.component.header.abmelden')}
                    </DropdownItem>,
                  ]}
                </DropdownMenu>
              </Dropdown>
            </NavbarItem>
          ) : (
            <>
              {/* Mobile: kompakter Anmelden-Button (Marcel 2026-08-27) */}
              <NavbarItem className="xl:hidden">
                <Button
                  as={RouterLink as any}
                  to="/login"
                  size="sm"
                  className="rounded-xl bg-[#3fced1] px-4 font-semibold text-black hover:bg-[#35b5b8]"
                >
                  {t('web.component.header.anmelden')}
                </Button>
              </NavbarItem>
              <NavbarItem className="hidden xl:flex gap-2">
                <Button
                  as={RouterLink as any}
                  to="/login"
                  color="default"
                  variant="light"
                  className="text-gray-300 hover:text-white"
                >
                  {t('web.component.header.einloggen')}
                </Button>
                <Button
                  as={RouterLink as any}
                  to="/register"
                  color="primary"
                  variant="solid"
                  className="bg-[#3fced1] hover:bg-[#35b5b8] text-black"
                >
                  {t('web.component.header.konto-erstellen')}
                </Button>
              </NavbarItem>
            </>
          )}
        </NavbarContent>

      </Navbar>

      {/* ================= MOBILE FULLSCREEN-MENÜ =================
          Eigenes Overlay statt NextUI NavbarMenu: Fullscreen, eigener Scroll,
          RouterLinks (kein Page-Reload), schließt bei Routenwechsel. */}
      {isMenuOpen && (
        <div className="fixed inset-0 z-[70] flex flex-col bg-zinc-950 xl:hidden">
          {/* Kopfzeile */}
          <div className="flex h-16 shrink-0 items-center justify-between border-b border-zinc-800 px-4">
            <RouterLink to="/" aria-label={t('web.component.footer.zur-startseite')} onClick={() => setIsMenuOpen(false)}>
              <img
                src="/DC_Logo_Typo_Blau_gross.png"
                alt={t('web.component.header.dartcloud-logo')}
                className="dc-logo-dark h-7 w-auto max-w-[120px] object-contain"
              />
              <img
                src="/DC_Logo_Typo_SchwarzBlau_gross.png"
                alt={t('web.component.header.dartcloud-logo')}
                className="dc-logo-light hidden h-7 w-auto max-w-[120px] object-contain"
              />
            </RouterLink>
            <button
              type="button"
              aria-label={t('web.component.header.menue-schliessen')}
              onClick={() => setIsMenuOpen(false)}
              className="flex h-11 w-11 items-center justify-center rounded-xl text-white active:bg-zinc-800"
            >
              <X size={24} />
            </button>
          </div>

          {/* Inhalt (scrollbar) */}
          <div className="flex-1 overflow-y-auto px-4 pb-10 pt-4">
            {/* Hauptnavigation */}
            <nav className="space-y-1">
              {menuItems.map((item) => {
                const active = isActivePath(item.href);
                return (
                  <RouterLink
                    key={item.name}
                    to={item.href}
                    className={`flex min-h-[48px] items-center rounded-xl px-3 text-base font-semibold ${
                      active ? 'border-l-[3px] border-[#3fced1] bg-[#3fced1]/10 text-white' : 'text-white active:bg-zinc-900'
                    }`}
                  >
                    {item.name}
                  </RouterLink>
                );
              })}
            </nav>

            {/* Mehr entdecken */}
            <p className="mb-2 mt-6 px-3 text-[11px] uppercase tracking-widest text-gray-500">
              {t('web.component.header.mehr-entdecken')}
            </p>
            <div className="grid grid-cols-2 gap-2">
              {moreDropdownItems.map((item) => {
                const Icon = item.icon;
                return (
                  <RouterLink
                    key={`mobile-${item.name}`}
                    to={item.href}
                    className="flex min-h-[64px] flex-col justify-center gap-1 rounded-xl border border-zinc-800 bg-zinc-900 px-3 py-2 active:bg-zinc-800"
                  >
                    <span className="flex items-center gap-2 text-sm font-semibold text-white">
                      <Icon size={15} className="shrink-0 text-[#3fced1]" />
                      <span className="truncate">{item.name}</span>
                    </span>
                    <span className="line-clamp-1 text-[11px] text-gray-500">{item.description}</span>
                  </RouterLink>
                );
              })}
            </div>

            {/* Lösungen (2026-09-20) */}
            <p className="mb-2 mt-6 px-3 text-[11px] uppercase tracking-widest text-gray-500">
              {t('web.component.header.loesungen')}
            </p>
            <div className="grid grid-cols-2 gap-2">
              {solutionsItems.map((item) => (
                <RouterLink
                  key={`mobile-solution-${item.href}`}
                  to={item.href}
                  className="flex min-h-[64px] flex-col justify-center gap-1 rounded-xl border border-zinc-800 bg-zinc-900 px-3 py-2 active:bg-zinc-800"
                >
                  <span className="truncate text-sm font-semibold text-white">{item.name}</span>
                  {item.description && <span className="line-clamp-1 text-[11px] text-gray-500">{item.description}</span>}
                </RouterLink>
              ))}
              <RouterLink
                to={solutionsHubHref}
                className="flex min-h-[64px] flex-col justify-center gap-1 rounded-xl border border-zinc-800 bg-zinc-900 px-3 py-2 active:bg-zinc-800"
              >
                <span className="flex items-center gap-2 text-sm font-semibold text-white">
                  <Sparkles size={15} className="shrink-0 text-[#3fced1]" />
                  <span className="truncate">{t('web.component.header.alle-loesungen')}</span>
                </span>
                <span className="line-clamp-1 text-[11px] text-gray-500">{t('web.component.header.dartcloud-fuer-vereine-spielstaetten')}</span>
              </RouterLink>
            </div>

            {/* Konto */}
            {isAuthenticated ? (
              <div className="mt-6 rounded-2xl border border-zinc-800 bg-zinc-900 p-3">
                <div className="flex items-center gap-3 px-1 pb-3">
                  <Avatar
                    src={avatarUrl || undefined}
                    name={initials}
                    showFallback
                    className="h-10 w-10 bg-[#3fced1] font-semibold text-black"
                  />
                  <div className="min-w-0 flex-col">
                    <p className="truncate text-sm font-semibold text-white">{displayName}</p>
                    {user?.email && <p className="truncate text-xs text-gray-400">{user.email}</p>}
                  </div>
                </div>
                <div className="space-y-0.5 border-t border-zinc-800 pt-2">
                  {[
                    { to: '/profil', icon: UserIcon, label: 'Profil' },
                    { to: '/favoriten', icon: Heart, label: 'Favoriten' },
                    { to: '/meine-anmeldungen', icon: ClipboardList, label: 'Meine Anmeldungen' },
                    { to: '/nachrichten', icon: MessageCircle, label: unreadCount > 0 ? `Nachrichten (${unreadCount})` : 'Nachrichten' },
                    { to: '/verwaltung', icon: Sliders, label: 'Verwaltung' },
                    ...(isAdmin ? [{ to: '/admin', icon: Shield, label: 'Admin-Bereich' }] : []),
                  ].map(({ to, icon: Icon, label }) => (
                    <RouterLink
                      key={to}
                      to={to}
                      className="flex min-h-[44px] items-center gap-3 rounded-lg px-2 text-gray-200 active:bg-zinc-800"
                    >
                      <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#3fced1]/10 text-[#3fced1]">
                        <Icon size={15} />
                      </span>
                      <span className="text-sm font-medium">{label}</span>
                    </RouterLink>
                  ))}
                  {/* Öffnet das Auswahl-Modal statt zu navigieren, daher kein RouterLink */}
                  <button
                    type="button"
                    onClick={() => {
                      setIsMenuOpen(false);
                      setIsCreateOpen(true);
                    }}
                    className="flex min-h-[44px] w-full items-center gap-3 rounded-lg px-2 text-gray-200 active:bg-zinc-800"
                  >
                    <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#3fced1]/10 text-[#3fced1]">
                      <Plus size={15} />
                    </span>
                    <span className="text-sm font-medium">{t('web.component.header.erstellen')}</span>
                  </button>
                </div>
                <Button
                  color="danger"
                  variant="flat"
                  className="mt-2 w-full"
                  onPress={() => {
                    setIsMenuOpen(false);
                    handleLogout();
                  }}
                  startContent={<LogOut size={16} />}
                >
                  {t('web.component.header.abmelden')}
                </Button>
              </div>
            ) : (
              <div className="mt-6 space-y-2">
                {/* Bewusst schlichte RouterLinks (kein NextUI-Button mit onPress):
                    Die Button+as+onPress-Kombination verschluckte die Navigation,
                    sodass man auf der aktuellen Seite hängen blieb. Das Menü
                    schließt sich ohnehin über den location.pathname-useEffect. */}
                <RouterLink
                  to="/login"
                  className="flex min-h-[48px] w-full items-center justify-center rounded-xl border border-zinc-700 px-3 text-base font-semibold text-white active:bg-zinc-900"
                >
                  {t('web.component.header.einloggen')}
                </RouterLink>
                <RouterLink
                  to="/register"
                  className="flex min-h-[48px] w-full items-center justify-center rounded-xl bg-[#3fced1] px-3 text-base font-semibold text-black active:bg-[#35b5b8]"
                >
                  {t('web.component.header.konto-erstellen')}
                </RouterLink>
              </div>
            )}

            {/* Theme-Umschalter (bewusst ganz unten) */}
            <button
              type="button"
              onClick={toggleTheme}
              className="mt-6 flex min-h-[48px] w-full items-center gap-3 rounded-xl border border-zinc-800 px-3 text-gray-300 active:bg-zinc-900"
            >
              <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#3fced1]/10 text-[#3fced1]">
                {theme === 'dark' ? <Sun size={17} /> : <Moon size={17} />}
              </span>
              <span className="text-sm font-medium">
                {theme === 'dark' ? t('web.header.theme.light') : t('web.header.theme.dark')}
              </span>
            </button>

            {/* Sprachumschalter (Mehrsprachigkeit 2026-09-17) */}
            <button
              type="button"
              onClick={() => { toggleLang(); }}
              className="mt-3 flex min-h-[48px] w-full items-center gap-3 rounded-xl border border-zinc-800 px-3 text-gray-300 active:bg-zinc-900"
            >
              <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#3fced1]/10 text-[11px] font-bold text-[#3fced1]">
                {lang.toUpperCase()}
              </span>
              <span className="text-sm font-medium">{t('web.header.language.label')}</span>
              <span className="ml-auto text-xs font-semibold text-gray-500">{lang === 'de' ? t('web.component.header.deutsch-english') : t('web.component.header.english-deutsch')}</span>
            </button>
          </div>
        </div>
      )}

      {/* App-Download-Hinweis als Popup (Desktop: Modal, Mobile: Vollbild) */}
      <AppDownloadPopup pathname={location.pathname} />

      {/* "Neuer Eintrag"-Auswahl (wie Flutter-CreateEntryModal) */}
      <CreateEntryModal isOpen={isCreateOpen} onClose={() => setIsCreateOpen(false)} />
    </>
  );
};

export default Header;